import {
  DependentStatus,
  type ErrorMessage,
  type InsuranceProvider,
  type InsuranceProviderFormSchema,
  JSONSchemaFieldType,
} from "../../types";
import { genericVisitValidation } from "./shared";

/**
 * NHIF
 */
const NHIFRegistrationSchema: InsuranceProviderFormSchema = {
  title: "NHIF Insurance details",
  description: "Form definition for NHIF insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "dependent",
      title: "Membership status",
      type: JSONSchemaFieldType.STRING,
      required: true,
      enum: [
        DependentStatus.MEMBER,
        DependentStatus.DEPENDENT_SPOUSE,
        DependentStatus.DEPENDENT_CHILD,
      ],
      enumTitles: ["Member", "Dependant (Spouse)", "Dependant (Child)"],
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,8}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "dependent",
      title: "Membership status",
      type: JSONSchemaFieldType.STRING,
      required: true,
      enum: [
        DependentStatus.MEMBER,
        DependentStatus.DEPENDENT_SPOUSE,
        DependentStatus.DEPENDENT_CHILD,
      ],
      meta: {
        enumTitles: ["Member", "Dependant (Spouse)", "Dependant (Child)"],
      },
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport Number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,8}$",
      required: true,
    },
  ],
};

const errMessagesNHIFRegistration: Record<string, ErrorMessage> = {
  insuranceNumber: {
    required: "Please enter a valid insurance number",
    pattern: "Membership number must be 7 or 8 alphanumeric characters",
  },
  nationalIdNumber: {
    required: "Please enter a valid national ID or passport number",
    pattern:
      "The national ID or passport number must be at least 7 alphanumeric characters",
  },
};

const NHIFRegistrationValidatorConfig = {
  errMessages: errMessagesNHIFRegistration,
};

/**
 * Linda Mama
 */

const LindaMamaRegistrationSchema: InsuranceProviderFormSchema = {
  title: "Linda Mama Insurance details",
  description: "Form definition for Linda Mama insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^M[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "expiryDate",
      title: "Expiry date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
  orderedProperties: [
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^M[a-zA-Z0-9]{7,}$",
      isRequired: true,
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport number",
      type: JSONSchemaFieldType.STRING,
      pattern: "^[a-zA-Z0-9]{7,}$",
      isRequired: true,
    },
    {
      name: "expiryDate",
      title: "Expiry date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
};

const errMessagesLindaMamaRegistration: Record<string, ErrorMessage> = {
  insuranceNumber: {
    required: "Please enter a valid insurance number",
    pattern:
      "Membership number must follow format M xxxxxxx (at least 7 alphanumeric characters)",
  },
  nationalIdNumber: {
    required: "Please enter a valid national ID or passport number",
    pattern:
      "The national ID or passport number must be at least 7 alphanumeric characters",
  },
};

const LindaMamaRegistrationValidatorConfig = {
  errMessages: errMessagesLindaMamaRegistration,
};

/**
 * Private
 */

const PrivateInsuranceKenyaRegistrationSchema: InsuranceProviderFormSchema = {
  title: "Private Insurance details",
  description: "Form definition for Private insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    {
      name: "schemeName",
      title: "Scheme name",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      required: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
  orderedProperties: [
    {
      name: "schemeName",
      title: "Scheme name",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership Number",
      type: JSONSchemaFieldType.STRING,
      isRequired: true,
    },
    {
      name: "expiryDate",
      title: "Expiry Date",
      type: JSONSchemaFieldType.STRING,
      format: "date",
    },
  ],
};

const errMessagesPrivateInsuranceRegistration: Record<string, ErrorMessage> = {
  schemeName: {
    required: "Please enter name of the insurance scheme",
  },
  insuranceNumber: {
    required: "Please enter insurance number",
  },
};

const PrivateInsuranceKenyaRegistrationValidatorConfig = {
  errMessages: errMessagesPrivateInsuranceRegistration,
};

/**
 * SHIF
 */

export const SHIFRegistrationSchema: InsuranceProviderFormSchema = {
  title: "SHIF Insurance details",
  description: "Form definition for SHIF insurance details",
  type: JSONSchemaFieldType.OBJECT,
  properties: [
    // TODO be removed once FE 'legacy' (non-MUI) dynamic forms no longer utilise the `properties` attribute
    {
      name: "dependent",
      title: "Membership status",
      type: JSONSchemaFieldType.STRING,
      required: true,
      enum: [
        DependentStatus.MEMBER,
        DependentStatus.DEPENDENT_SPOUSE,
        DependentStatus.DEPENDENT_CHILD,
      ],
      enumTitles: ["Member", "Dependant (Spouse)", "Dependant (Child)"],
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/dlWFZC/1
      pattern: "^[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/whrgT5/1
      pattern: "^[a-zA-Z0-9]{7,8}$",
      required: true,
    },
  ],
  orderedProperties: [
    {
      name: "dependent",
      title: "Membership status",
      type: JSONSchemaFieldType.STRING,
      required: true,
      enum: [
        DependentStatus.MEMBER,
        DependentStatus.DEPENDENT_SPOUSE,
        DependentStatus.DEPENDENT_CHILD,
      ],
      meta: {
        enumTitles: ["Member", "Dependant (Spouse)", "Dependant (Child)"],
      },
    },
    {
      name: "nationalIdNumber",
      title: "National ID / Passport Number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/dlWFZC/1
      pattern: "^[a-zA-Z0-9]{7,}$",
      required: true,
    },
    {
      name: "insuranceNumber",
      title: "Membership number",
      type: JSONSchemaFieldType.STRING,
      // https://regex101.com/r/pOATZz/1
      pattern: "^CR[0-9]{13}-[0-9]$",
      required: true,
    },
  ],
};

const errMessagesSHIFRegistration: Record<string, ErrorMessage> = {
  insuranceNumber: {
    required: "Please enter a valid insurance number",
    pattern: "Membership number must be of the pattern CRxxxxxxxxxxxxx-x",
  },
  nationalIdNumber: {
    required: "Please enter a valid national ID or passport number",
    pattern:
      "The national ID or passport number must be at least 7 alphanumeric characters",
  },
};

export const SHIFRegistrationValidatorConfig = {
  errMessages: errMessagesSHIFRegistration,
};

/**
 * Final output
 */

export const kenyaInsuranceSchemas: InsuranceProvider[] = [
  {
    id: "NHIF",
    insuranceType: "NHIF",
    name: "NHIF",
    registrationValidation: {
      formSchema: NHIFRegistrationSchema,
      validatorConfig: NHIFRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "LM",
    insuranceType: "LM",
    name: "Linda Mama",
    registrationValidation: {
      formSchema: LindaMamaRegistrationSchema,
      validatorConfig: LindaMamaRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "Private",
    insuranceType: "Private",
    name: "Private",
    registrationValidation: {
      formSchema: PrivateInsuranceKenyaRegistrationSchema,
      validatorConfig: PrivateInsuranceKenyaRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
  {
    id: "SHIF",
    insuranceType: "SHIF",
    name: "SHIF",
    registrationValidation: {
      formSchema: SHIFRegistrationSchema,
      validatorConfig: SHIFRegistrationValidatorConfig,
    },
    visitValidation: genericVisitValidation,
  },
];
